/**
 * Pure helpers for the Add-Equipment dialog (spec v2 §5).
 *
 * The dialog is split into a pure helpers module (here) and a stateful React
 * component (AddEquipmentDialog.tsx) so we can unit-test the bits that drive
 * the spec — label generation, batch/unit detection, validation rules,
 * property submission ordering — without spinning up a DOM.
 */

import type { PropertyDefinition } from '../../hooks/useQueries'

/** Asset type subset we need from the ontology catalog. */
export interface AssetTypeMeta {
  uid: string
  code: string
  name?: Record<string, string> | null
  emoji?: string | null
  ordering?: number | null
  /** Parent type in the inheritance hierarchy (e.g. equipment subtype → trade → equipment). */
  inherits_from_asset_type_uid?: string | null
  /** `properties` blob from the asset_type row — carries the `multiple` flag. */
  properties?: { multiple?: boolean } | null
  flags?: string[] | null
}

/** Find the `equipment` root in the asset-types catalog (by code). */
export function findEquipmentRoot(allAssetTypes: AssetTypeMeta[]): AssetTypeMeta | null {
  return allAssetTypes.find((at) => at.code === 'equipment') ?? null
}

/**
 * Discipline (trade suffix) of an asset type, derived from its code:
 * `trade_struct` → `struct`, `struct_foundation` → `struct`,
 * `hvac_boiler` → `hvac`. Null when the code has no discipline prefix.
 */
export function disciplineOfType(at: AssetTypeMeta | null | undefined): string | null {
  const code = at?.code
  if (!code) return null
  if (code.startsWith('trade_')) return code.slice('trade_'.length) || null
  const i = code.indexOf('_')
  return i > 0 ? code.slice(0, i) : null
}

/**
 * "Monolithic" building-envelope trades (structure, roof, facade). Their items
 * are single building elements: no quantity, brand or capacity is captured.
 */
const MONOLITHIC_DISCIPLINES = new Set(['struct', 'roof', 'facade'])

export function isMonolithicType(at: AssetTypeMeta | null | undefined): boolean {
  const d = disciplineOfType(at)
  return d !== null && MONOLITHIC_DISCIPLINES.has(d)
}

/**
 * Direct children of `parentUid` in the inheritance tree, sorted by
 * `ordering` ascending then code. Drives the cascading type-picker dropdowns:
 * each selection at level k narrows the options at level k+1.
 */
export function childTypesOf(
  allAssetTypes: AssetTypeMeta[],
  parentUid: string,
): AssetTypeMeta[] {
  const out = allAssetTypes.filter((at) => at.inherits_from_asset_type_uid === parentUid)
  out.sort((a, b) => {
    const ao = a.ordering ?? Number.POSITIVE_INFINITY
    const bo = b.ordering ?? Number.POSITIVE_INFINITY
    if (ao !== bo) return ao - bo
    return a.code.localeCompare(b.code)
  })
  return out
}

/**
 * Returns true when assets of this type carry a quantity instead of being
 * created one-per-row. Drives the batch/unit branch in the dialog form.
 *
 * Source of truth: `asset_types.properties.multiple`
 * (docs/domain/ontology-and-assets.md). The asset_types ontology JSON also sometimes encodes the same
 * concept under the `flags` array as `multiple` — we accept both.
 */
export function isBatchType(at: AssetTypeMeta | null | undefined): boolean {
  if (!at) return false
  if (at.properties?.multiple === true) return true
  if (Array.isArray(at.flags) && at.flags.includes('multiple')) return true
  return false
}

/** Locale-resolved label for an asset_type, with code fallback. */
export function assetTypeLabel(
  at: AssetTypeMeta | null | undefined,
  locale: string,
): string {
  if (!at) return ''
  const name = at.name ?? null
  if (name) {
    const v = name[locale] || name.en || name.fr || Object.values(name)[0]
    if (v) return v
  }
  return at.code
}

/**
 * Identify the trades in an asset_types list. The list is expected to be the
 * subtree of `equipment` — typically what `GET /asset-types?inherits_from=equipment`
 * returns. A trade is a `trade_*`-prefixed code (current ontology convention);
 * everything else in the subtree is an LFNM leaf.
 *
 * The convention-based detection is necessary because the API response does
 * not surface `inherits_from_asset_type_uid` (the FK lives in the DB column
 * `inherits_from_asset_type_id` but is not serialized on `AssetType`). Using
 * the back-side filter handles the "is descendant of equipment" question;
 * code prefix handles the "is a trade rather than an LFNM" question.
 */
export function findTrades(equipmentSubtree: AssetTypeMeta[]): AssetTypeMeta[] {
  const trades = equipmentSubtree.filter((at) => at.code.startsWith('trade_'))
  trades.sort((a, b) => {
    const ao = a.ordering ?? Number.POSITIVE_INFINITY
    const bo = b.ordering ?? Number.POSITIVE_INFINITY
    if (ao !== bo) return ao - bo
    return a.code.localeCompare(b.code)
  })
  return trades
}

/**
 * Extract the discipline suffix from a trade code: `trade_hvac` → `hvac`.
 * Returns null when the code is not a trade.
 */
export function tradeSuffix(code: string): string | null {
  if (!code.startsWith('trade_')) return null
  const suffix = code.slice('trade_'.length)
  return suffix === '' ? null : suffix
}

/**
 * For a given trade, return every asset_type in the equipment subtree whose
 * code matches the trade's discipline suffix — including the trade itself.
 * Used by the Type picker so the user can pick the trade directly OR a
 * specific LFNM leaf when seeded.
 *
 * Convention: LFNM types under a trade carry the trade's suffix as their
 * code prefix. `trade_hvac` matches `hvac_boiler_gas`, `hvac_chiller`, etc.
 * Falls back to identity (just the trade itself) when the suffix is missing.
 */
export function descendantsOfTrade(
  equipmentSubtree: AssetTypeMeta[],
  tradeUid: string,
): AssetTypeMeta[] {
  const trade = equipmentSubtree.find((at) => at.uid === tradeUid)
  if (!trade) return []
  const suffix = tradeSuffix(trade.code)
  if (!suffix) return [trade]
  const prefix = `${suffix}_`
  const out: AssetTypeMeta[] = [trade]
  for (const at of equipmentSubtree) {
    if (at.uid === tradeUid) continue
    if (at.code.startsWith(prefix)) out.push(at)
  }
  return out
}

/**
 * Compose the autosuggested display name for a freshly-created equipment.
 * - unit (single):  `<type.label>`            optional name override allowed
 * - batch:          `<type.label> × <qty> · <location.label>`
 *
 * The label is what the equipment listing renders when `equipment.name` is
 * null; it is *not* persisted as `name`. The dialog persists `name` only when
 * the user explicitly types one.
 */
export function suggestedDisplayLabel(args: {
  assetType: AssetTypeMeta | null | undefined
  quantity?: number | null
  locationLabel?: string | null
  locale: string
}): string {
  const typeLabel = assetTypeLabel(args.assetType, args.locale)
  if (!typeLabel) return ''
  if (isBatchType(args.assetType)) {
    const qty = args.quantity ?? 1
    const loc = args.locationLabel ? ` · ${args.locationLabel}` : ''
    return `${typeLabel} × ${qty}${loc}`
  }
  return typeLabel
}

/** Result of validating the dialog state before submission. */
export interface ValidationResult {
  ok: boolean
  /** Field-keyed errors. Keys: 'type', 'quantity', 'location'. */
  errors: Record<string, string>
}

/**
 * Validate the dialog state. Pure — returned errors are i18n keys, resolved by
 * the caller. Quantity is only required when the picked asset type is a batch
 * type (`properties.multiple === true` / `flags` includes `multiple`); for
 * unit types it is irrelevant — the dialog defaults it to 1 internally and
 * the field isn't even rendered.
 */
export function validate(state: {
  typeUid: string
  quantity: number | null
  locationUid: string
  assetType?: AssetTypeMeta | null
}): ValidationResult {
  const errors: Record<string, string> = {}
  if (!state.typeUid) errors.type = 'add_equipment.error_type_required'
  if (!state.locationUid) errors.location = 'add_equipment.error_location_required'
  if (isBatchType(state.assetType) && (!state.quantity || state.quantity < 1)) {
    errors.quantity = 'add_equipment.error_quantity_required'
  }
  return { ok: Object.keys(errors).length === 0, errors }
}

/**
 * Decide which property_definitions to render in the dynamic field block at
 * creation time.
 *
 * The relevant properties per type are now curated in the ontology itself
 * (each type only associates the properties that make sense for it), so this
 * helper only needs to drop the two truly-structural fields plus any
 * system-managed property.
 *
 * Ordering: `display_order` ascending if present, then `name.en` ascending.
 */
export function selectDynamicProperties(defs: PropertyDefinition[]): PropertyDefinition[] {
  const RESERVED_CODES = new Set([
    // Condition is set outside the creation flow (a new item is new).
    'equipment_condition',
    // Location is a structural FK on the asset row, not a property.
    'location_room_uid',
  ])
  const out = defs.filter((d) => {
    if (!d.code) return false
    if (RESERVED_CODES.has(d.code)) return false
    if (Array.isArray(d.flags) && d.flags.includes('system_managed')) return false
    return true
  })
  out.sort((a, b) => {
    const ao = (a as { display_order?: number }).display_order ?? Number.POSITIVE_INFINITY
    const bo = (b as { display_order?: number }).display_order ?? Number.POSITIVE_INFINITY
    if (ao !== bo) return ao - bo
    const an = (a.name as Record<string, string> | undefined)?.en ?? a.code ?? ''
    const bn = (b.name as Record<string, string> | undefined)?.en ?? b.code ?? ''
    return an.localeCompare(bn)
  })
  return out
}

/**
 * Compose the property write payload. Filters out empty/null values so we
 * don't write zeros or empty strings.
 */
export function composePropertyWrites(
  values: Record<string, unknown>,
): Array<{ property_uid: string; value: unknown }> {
  const out: Array<{ property_uid: string; value: unknown }> = []
  for (const [propertyUid, value] of Object.entries(values)) {
    if (value === null || value === undefined) continue
    if (typeof value === 'string' && value.trim() === '') continue
    if (Array.isArray(value) && value.length === 0) continue
    out.push({ property_uid: propertyUid, value })
  }
  return out
}
